/**
 * Parse a cURL command into connection configuration
 */

interface ParsedCurl {
  base_url: string;
  method: string;
  headers: Record<string, string>;
  auth_type: string;
  auth_config: Record<string, unknown>;
  config: Record<string, unknown>;
  body?: string;
}

export function parseCurlCommand(curlCommand: string): ParsedCurl {
  // Normalize the command (handle line continuations and multiple spaces)
  const normalized = curlCommand
    .replace(/\\\s*\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Check if it starts with curl
  if (!normalized.toLowerCase().startsWith("curl")) {
    throw new Error("Input does not appear to be a cURL command");
  }

  const result: ParsedCurl = {
    base_url: "",
    method: "GET",
    headers: {},
    auth_type: "none",
    auth_config: {},
    config: {},
  };

  // Extract URL - look for quoted or unquoted URL
  const urlPatterns = [
    /curl\s+(?:-[A-Za-z]+\s+)*['"]?(https?:\/\/[^\s'"]+)['"]?/i,
    /['"]?(https?:\/\/[^\s'"]+)['"]?/,
  ];

  for (const pattern of urlPatterns) {
    const urlMatch = normalized.match(pattern);
    if (urlMatch && urlMatch[1]) {
      result.base_url = urlMatch[1].replace(/['"]$/, "");
      break;
    }
  }

  if (!result.base_url) {
    throw new Error("Could not find URL in cURL command");
  }

  // Extract base URL (remove path and query params for base)
  try {
    const url = new URL(result.base_url);
    result.base_url = `${url.protocol}//${url.host}`;
    if (url.pathname && url.pathname !== "/") {
      result.config.initial_path = url.pathname;
    }
  } catch {
    // Keep the URL as-is if parsing fails
  }

  // Extract method (-X or --request)
  const methodMatch = normalized.match(/-X\s+['"]?(\w+)['"]?/i) ||
    normalized.match(/--request\s+['"]?(\w+)['"]?/i);
  if (methodMatch) {
    result.method = methodMatch[1].toUpperCase();
  }

  // Extract headers (-H or --header)
  const headerRegex = /(?:-H|--header)\s+['"]([^'"]+)['"]/g;
  let headerMatch;
  while ((headerMatch = headerRegex.exec(normalized)) !== null) {
    const header = headerMatch[1];
    const colonIndex = header.indexOf(":");
    if (colonIndex > 0) {
      const key = header.substring(0, colonIndex).trim();
      const value = header.substring(colonIndex + 1).trim();
      
      // Check for authentication headers
      if (key.toLowerCase() === "authorization") {
        const authValue = value.toLowerCase();
        if (authValue.startsWith("bearer ")) {
          result.auth_type = "bearer_token";
          result.auth_config.token = value.substring(7).trim();
        } else if (authValue.startsWith("basic ")) {
          result.auth_type = "basic_auth";
          try {
            const decoded = atob(value.substring(6).trim());
            const [username, password] = decoded.split(":");
            result.auth_config.username = username;
            result.auth_config.password = password || "";
          } catch {
            result.auth_config.encoded = value.substring(6).trim();
          }
        } else {
          result.headers[key] = value;
        }
      } else if (key.toLowerCase() === "x-api-key" || key.toLowerCase() === "apikey") {
        result.auth_type = "api_key";
        result.auth_config.api_key = value;
        result.auth_config.api_key_name = key;
        result.auth_config.api_key_location = "header";
      } else {
        result.headers[key] = value;
      }
    }
  }

  // Extract data/body (-d, --data, --data-raw, --data-binary)
  const dataMatch = normalized.match(/(?:-d|--data(?:-raw|-binary)?)\s+['"]([^'"]+)['"]/);
  if (dataMatch) {
    result.body = dataMatch[1];
    result.config.sample_body = dataMatch[1];
  }

  // Extract user credentials (-u or --user)
  const userMatch = normalized.match(/(?:-u|--user)\s+['"]?([^'"\s]+)['"]?/);
  if (userMatch) {
    const [username, password] = userMatch[1].split(":");
    result.auth_type = "basic_auth";
    result.auth_config.username = username;
    result.auth_config.password = password || "";
  }

  // Check for insecure flag
  if (normalized.includes("-k") || normalized.includes("--insecure")) {
    result.config.verify_ssl = false;
  }

  return result;
}
